import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { HttpClientService } from '../../../essentials/http/http-client.service';
import { BannerData } from '@fcp/lib-ui';

@Injectable({
  providedIn: 'root',
})
export class BannerContainerService extends HttpClientService {
  private host =
    environment.configCMS.endPointAPI + environment.configCMS.endPointAPP;

  constructor(private router: Router, private http: HttpClient) {
    super(http);
  }

  public getBanner(id: string): Observable<any> {
    return this.getBannerContents(id).pipe(
      map((res) => this.mapBanner(this.extractResult(res))),
      catchError((error) => {
        console.log('error', error);
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  private extractResult(res) {
    return this.mapResponseGraphQL(res)[0].flatData;
  }

  private mapResponseGraphQL(response: HttpResponse<any> | any) {
    if (response?.body?.errors) {
      console.warn('response?.body?.errors', response?.body?.errors);
    }
    if (response?.body?.data) {
      const key = Object.keys(response.body.data).pop();
      return response.body.data[key];
    }
    if (response?.data) {
      const key = Object.keys(response.data).pop();
      return response.data[key];
    }
  }

  private getBannerContents(id: string): Observable<any> {
    const payload = {
      query: `
      {
        queryBannersContents(filter: "id eq '${id}'") {
          flatData {
            profiles
            titulo
            redeCredenciada
            hasMargin
            descricao
            imagem {
              url
            }
            botoes {
              titulo
              cor
              uRL
              page {
                data {
                  slug { iv }
                }
              }
              target {
                ... on Formulario {
                  id
                }
              }
              modal {
                id
              }
            }
            links {
              titulo
              uRL
              pagina {
                flatData {
                  slug
                }
              }
              modal {
                id
              }
              imagem {
                url
              }
            }
            menu {
              titulo
              uRL
              modal {
                id
              }
              pagina {
                flatData {
                  slug
                }
              }
            }
          }
        }
      }`,
    };

    return this.send(payload);
  }

  private send(payload): Observable<any> {
    const url = `${this.host}graphql`;
    return this.post(url, payload);
  }

  private mapBanner(banner) {
    const hasButton = banner.botoes && banner.botoes.length > 0;
    let buttons = [];
    if (hasButton) {
      buttons = banner.botoes.map((button) => {
        let externalURL = false;
        if (button.uRL) {
          if (button.uRL.indexOf('http') >= 0) {
            externalURL = true;
          }
        }

        return {
          title: button.titulo ? button.titulo : '',
          color: button.cor ? button.cor : '',
          url: button.uRL ? button.uRL : '',
          modal: button.modal && button.modal[0] ? button.modal[0].id : null,
          target: button.target && button.target[0] ? button.target[0].id : '',
          router: externalURL ? null : button.uRL ? button.uRL : null,
          hrefTarget: externalURL ? '_blank' : null,
          page: button.page?.length
            ? '/paginas/' + button.page[0].data.slug.iv
            : '',
        };
      });
    }
    const hasMenu = !!banner.menu && banner.menu.length > 0;
    let menu = [];
    if (hasMenu) {
      menu = banner.menu.map((menuItem) => {
        return {
          title: menuItem.titulo ? menuItem.titulo : '',
          url: menuItem.uRL ? menuItem.uRL : '',
          modal:
            menuItem.modal && menuItem.modal[0] ? menuItem.modal[0].id : null,
          page: menuItem.pagina
            ? menuItem.pagina.length > 0
              ? '/paginas/' + menuItem.pagina[0].flatData.slug
              : null
            : null,
        };
      });
    }
    const hasLinks = !!banner.links && banner.links.length > 0;
    let links = [];
    if (hasLinks) {
      links = banner.links.map((link) => {
        return {
          title: link.titulo,
          icon: link?.imagem ? link.imagem[0].url : null,
          url: link?.pagina
            ? '/paginas/' + link.pagina[0].flatData.slug
            : link?.uRL
            ? '/' + link.uRL
            : null,
          modal: link?.modal ? link.modal[0] : null,
        };
      });
    }
    let profiles;
    const hasProfiles = !!banner.profiles;
    if (hasProfiles) {
      profiles = this.getConfigProfiles().pipe(
        map((res) => this.extractResult(res)),
        map((res) => {
          return {
            labelLink: res.labelLink,
            content: this.getProfiles().toPromise(),
          };
        })
      );
    }
    const result = {
      title: banner.titulo ? banner.titulo : null,
      description: banner.descricao ? banner.descricao : null,
      image: banner.imagem && banner.imagem[0] ? banner.imagem[0].url : '',
      center: buttons.length > 1 ? true : false,
      hasBottomNav: banner.redeCredenciada ? banner.redeCredenciada : false,
      hasMargin: banner.hasMargin ? banner.hasMargin : false,
      hasProfiles,
      profiles,
      buttons,
      links,
      menu,
    } as BannerData;
    return result;
  }

  private getConfigProfiles(): Observable<any> {
    const params = {
      query: `{
        queryConfiguracaoHomePerfisContents {
          flatData{
            labelLink
          }
        }
      }`,
    };
    return this.send(params);
  }

  public getProfiles() {
    const params = {
      query: this.profilesQuery(),
    };
    return this.send(params).pipe(
      map((res) => this.mapResponseGraphQL(res)),
      map((profiles) => {
        return profiles.map((item) => {
          const data = item.data;
          const services = data['servicos'].iv.map((service) => {
            const modal =
              service.modal && service.modal[0]
                ? service.modal && service.modal[0]
                : false;
            const externalURL = service.url.indexOf('http') >= 0;
            return {
              title: service.titulo,
              url: externalURL ? '/' + service.url : null,
              router: externalURL ? null : '/' + service.url,
              hrefTarget: externalURL ? '_blank' : null,
              modal: modal ? modal.id : '',
              isCredential:
                modal &&
                modal.data &&
                modal.data.tipoConteudo &&
                modal.data.tipoConteudo.iv === 'Rede Credenciada',
            };
          });
          const typenamevalido = data['linkBotaoPagina'] !== null && data['linkBotaoPagina'].iv !== null;
          const typename = typenamevalido
            ? data['linkBotaoPagina'].iv[0].__typename
            : null;
          let pageBase = null;
          let pageRoute = null;
          if (typenamevalido) {
            const pageData = data['linkBotaoPagina'].iv[0].data;
            if (typename) {
              if (typename === 'Filtros') {
                pageBase = '/filtro/';
              } else {
                pageBase = '/paginas/';
              }
              const pageSlug = pageData.nome
                ? pageData.nome.iv
                : pageData.slug.iv;
              pageRoute = pageBase + pageSlug;
            }
          }
          return {
            title: data['titulo'].iv,
            button: [
              {
                title: data['descricaoBotao'].iv,
                butttonBackgroundColor: data?.butttonBackgroundColor?.iv
                  ? data?.butttonBackgroundColor?.iv
                  : '',
                buttonBorderColor: data?.buttonBorderColor?.iv
                  ? data?.buttonBorderColor?.iv
                  : '',
                buttonTextColor: data?.buttonTextColor?.iv
                  ? data?.buttonTextColor?.iv
                  : '',
                url: data['linkBotaoUrl'] ? data['linkBotaoUrl'].iv : null,
                openUrl: data['abrirURL'] ? data['abrirURL'].iv : null,
                page: pageRoute,
                color: 'Básico',
              },
            ],
            icon: data['icone'].iv[0].url,
            page: pageRoute,
            url: data['linkBotaoUrl'] ? data['linkBotaoUrl'].iv : null,
            modal: (data['modal'] && data['modal'].iv) ? data['modal'].iv[0].id : null,
            services,
            linkPageMoreServices: data['linkPageMaisServicos']
              ? `/paginas/${data['linkPageMaisServicos'].iv[0].data.slug.iv}`
              : null,
          };
        });
      })
    );
  }

  private profilesQuery() {
    return `
      {
        queryHomePerfisContents(orderby: "data/Ordem/iv") {
          data {
            descricaoBotao {iv}
            butttonBackgroundColor {iv}
            buttonBorderColor {iv}
            buttonTextColor {iv}
            icone {
              iv {
                url
              }
            }
            linkBotaoPagina {
              iv {
                __typename
                ... on Paginas {
                  data {
                    slug {
                      iv
                    }
                  }
                }
                ... on Filtros {
                  data {
                    nome {
                      iv
                    }
                  }
                }
              }
            }
            linkPageMaisServicos {
              iv {
                ... on Paginas {
                  data {
                    slug {
                      iv
                    }
                  }
                }
              }
            }
            linkBotaoUrl {
              iv
            }
            abrirURL {
              iv
            }
            modal {
              iv {
                id
              }
            }
            servicos {
              iv {
                titulo
                url
                modal {
                  id
                  data{
                    tipoConteudo {
                      iv
                    }
                  }
                }
              }
            }
            titulo {iv}
          }
        }
      }
    `;
  }
}
